import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, Calendar, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import { parseMarkdown } from '../utils/markdownParser';
import { cookieConsent } from '../lib/cookie-consent';

export function CookiePolicyPage() {
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleManageCookies = () => {
    try {
      cookieConsent.show();
    } catch (error) {
      console.error('Error opening cookie preferences:', error);
      // Fallback - try to show preferences modal if main modal fails
      try {
        cookieConsent.showConsentModal();
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
      }
    }
  };

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const response = await fetch('/Policies/rulevia-cookie-policy-updated.md');
        if (!response.ok) {
          throw new Error('Failed to load Cookie Policy');
        }
        const content = await response.text();
        setMarkdownContent(content);
      } catch (err) {
        setError('Failed to load Cookie Policy. Please try again later.');
        console.error('Error loading markdown:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMarkdown();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Cookie Policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Cookie className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Document</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead {...getPageSEO('/cookies')} />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
              <div className="w-px h-6 bg-gray-300"></div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Cookie Policy</h1>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>Last updated: 19.08.2025</span>
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/')}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Return to Rulevia
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 lg:p-12"
        >
          {parseMarkdown(markdownContent, { className: 'legal-document' })}
        </motion.div>

        {/* Cookie Management Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8"
        >
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Manage Cookie Preferences</h3>
                  <p className="text-sm text-gray-600">Update your cookie consent settings at any time</p>
                </div>
              </div>
              <button
                onClick={handleManageCookies}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 font-medium flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <Cookie className="w-4 h-4" />
                <span>Cookie Settings</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Footer Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
        >
          <div className="text-sm text-gray-500">
            <p>This cookie policy is effective as of the date shown above.</p>
            <p>For cookie questions, contact us at <a href="mailto:support@rulevia.com" className="text-purple-600 hover:text-purple-700">support@rulevia.com</a></p>
          </div>
          
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/terms')}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Terms of Service
            </button>
            <button
              onClick={() => navigate('/privacy')}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Privacy Policy
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}